import {
  CLUB_MODULE_FEATURE_KEYS,
  TierFeatureKey,
  canAccessFeature,
  type ClubPlanTier,
  type SubscriptionTier,
} from '../constants/planFeatures';

/** Organization subscription entitlements synced from Stripe subscription items. */
export interface OrgEntitlements {
  /** True when the organization has the club_management module add-on or a club plan item */
  clubManagement: boolean;
  /** True when the organization subscription includes an academy product line */
  academyManagement: boolean;
  /** Academy tier from subscription_tier (defaults to starter when absent). */
  tier: SubscriptionTier;
  /** Club tier from club_plan_tier, falling back to academy tier for legacy add-on customers. */
  clubTier: ClubPlanTier | SubscriptionTier;
}

const CLUB_MODULE_FEATURES = new Set<TierFeatureKey>(CLUB_MODULE_FEATURE_KEYS);

function isClubModuleFeature(feature: TierFeatureKey): boolean {
  return CLUB_MODULE_FEATURES.has(feature);
}

/**
 * Resolve feature access from organization entitlements and optional academy tier override.
 * Club module keys require `clubManagement`; academy keys fall back to tier rank via canAccessFeature.
 * MULTI_LOCATION passes when tier is Professional+ or club_management is enabled.
 */
export function hasEntitlement(
  orgEntitlements: OrgEntitlements,
  feature: TierFeatureKey,
  tier: SubscriptionTier = orgEntitlements.tier
): boolean {
  if (isClubModuleFeature(feature)) {
    return orgEntitlements.clubManagement;
  }

  if (feature === TierFeatureKey.MULTI_LOCATION || feature === TierFeatureKey.MATCH_PLAY) {
    return orgEntitlements.clubManagement || canAccessFeature(tier, feature);
  }

  return canAccessFeature(tier, feature);
}

/**
 * Club-surface entitlement check using clubTier for tier-gated club features (e.g. branded subdomain).
 */
export function hasClubEntitlement(
  orgEntitlements: OrgEntitlements,
  feature: TierFeatureKey
): boolean {
  if (isClubModuleFeature(feature)) {
    return orgEntitlements.clubManagement;
  }

  if (feature === TierFeatureKey.MULTI_LOCATION || feature === TierFeatureKey.MATCH_PLAY) {
    return (
      orgEntitlements.clubManagement ||
      canAccessFeature(orgEntitlements.clubTier as SubscriptionTier, feature)
    );
  }

  if (feature === TierFeatureKey.BRANDED_SUBDOMAIN) {
    return canAccessFeature(orgEntitlements.clubTier as SubscriptionTier, feature);
  }

  return canAccessFeature(orgEntitlements.clubTier as SubscriptionTier, feature);
}

/** Build OrgEntitlements from subscription row fields. */
export function buildOrgEntitlements(input: {
  clubManagement: boolean;
  academyManagement?: boolean;
  tier?: SubscriptionTier | null;
  clubPlanTier?: ClubPlanTier | null;
}): OrgEntitlements {
  const tier = input.tier ?? 'starter';
  const clubTier = input.clubPlanTier ?? tier;
  return {
    clubManagement: input.clubManagement,
    academyManagement: input.academyManagement ?? false,
    tier,
    clubTier,
  };
}
